import React, { JSX, ReactElement, use } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MonitoringIcon from 'shared/components/monitoring/monitoring-icon';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import { toMonitoringType } from 'shared/enums/fault-type';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import { DeviceType } from 'shared/models/device-type';

import LinkableDevice from 'features/device/linkable-device/domain/models/linkable-device';
import { DeviceLinkProvider } from 'features/device/providers/device-link-provider/device-link-provider';
import { ResidentDevice } from 'features/residents/models/resident-device';
import DeviceInfoModal from 'features/residents/resident-settings/components/resident-setting-details/components/devices-accordion/components/details-devices/components/device-info-modal/device-info-modal';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import DeviceLinkModal from './components/device-link-modal/device-link-modal';
import useDetailsDevices from './hooks/use-details-devices';
import styles from './details-devices.module.scss';

type ResidentDeviceRowItem = ResidentDevice & {
  isLinked: boolean;
};

const DetailsDevices = (): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings', { keyPrefix: 'details' });
  const { t: tDeviceTypes } = useTranslation('deviceTypes');
  const { hasPermission } = usePermissionAuthorization();

  const {
    detailEditState: { viewingMode, residentSettingEditForm, currentResidentSetting },
  } = use(ResidentSettingsProviderContext);

  const {
    linkDevicesModalIsOpen,
    unlinkedDevices,
    onDevicesLinkClicked,
    onUnlinkDeviceClicked,
    openLinkDevicesModal,
    closeLinkDevicesModal,
  } = useDetailsDevices();

  const renderIcon = (device: ResidentDevice): JSX.Element => {
    const monitoringType: MonitoringType | undefined = toMonitoringType(device.fault);
    return monitoringType ? <MonitoringIcon type={monitoringType} withTooltip visible size={16} /> : <></>;
  };

  const linkedDevices = residentSettingEditForm.getValues('devices') ?? [];
  const isDesoDeviceAlreadyLinked = linkedDevices.some((d) => d.type === DeviceType.DesoResidentTransmitter);

  const isUnlinkableButtonDisabled = (device: ResidentDevice): boolean => {
    const unlinkableDeviceTypes = [
      DeviceType.G1,
      DeviceType.G2,
      DeviceType.G2Transmitter,
      DeviceType.DesoClient,
      DeviceType.DesoResidentTransmitter,
      DeviceType.UniversalAlarmTransmitter,
    ];

    const isG2DevicePaired = device.type === DeviceType.G2Transmitter && !device.gatewayId && !!device.systemId;

    return device.type === DeviceType.G2Transmitter ? isG2DevicePaired : !unlinkableDeviceTypes.includes(device.type as DeviceType);
  };

  const renderLinkingAction = (device: ResidentDevice): ReactElement => {
    const isRelinkDisabled = device.type === DeviceType.DesoResidentTransmitter && isDesoDeviceAlreadyLinked;
    const isCurrentDeviceLinked = linkedDevices?.some((linkedDevice) => linkedDevice.id === device.id);

    if (hasPermission(Permission.LinkDevice) && isCurrentDeviceLinked) {
      return (
        <>
          {viewingMode !== 'viewing' && (
            <Tooltip title={isUnlinkableButtonDisabled(device) ? t('unlinkDisabledTooltip') : ''}>
              <span>
                <Button
                  startIcon={<LinkOffOutlinedIcon />}
                  onClick={() => onUnlinkDeviceClicked(device)}
                  variant="text"
                  disabled={isUnlinkableButtonDisabled(device)}
                >
                  {t('unlinkButtonText')}
                </Button>
              </span>
            </Tooltip>
          )}
        </>
      );
    }

    return (
      <>
        {viewingMode !== 'viewing' && (
          <Tooltip title={isRelinkDisabled ? t('relinkDisabledTooltip') : ''}>
            <span>
              <Button
                disabled={isRelinkDisabled}
                startIcon={<LinkOutlined />}
                onClick={() => onDevicesLinkClicked([device])}
                variant="text"
              >
                {t('relinkButtonText')}
              </Button>
            </span>
          </Tooltip>
        )}
      </>
    );
  };

  const columns: ColumnDefinition<ResidentDeviceRowItem, NestedKeyof<ResidentDeviceRowItem>>[] = [
    {
      key: 'name',
      label: t('devicesTable.column.device'),
      renderCustomContentProvider: (item) => (
        <div className={styles.nameColumn}>
          <Typography className={item.isLinked ? '' : styles.unlinkedDevice} variant="subtitle1">
            {item.name}
          </Typography>
        </div>
      ),
      disableSort: true,
    },
    {
      key: 'type',
      label: t('devicesTable.column.type'),
      renderCustomContentProvider: (item) => (
        <div className={styles.typeColumn}>
          <div>
            <Typography className={item.isLinked ? '' : styles.unlinkedDevice} variant="subtitle1">
              {item.displayType ?? tDeviceTypes(item.type)}
            </Typography>
          </div>
          <div>
            {[DeviceType.G2, DeviceType.G2Transmitter, DeviceType.DesoResidentTransmitter, DeviceType.DesoClient].includes(
              item.type as DeviceType,
            ) && <DeviceInfoModal type={item.type as DeviceType} displayType={item.displayType} />}
          </div>
        </div>
      ),
      disableSort: true,
    },
    {
      key: 'fault',
      label: '',
      renderCustomContentProvider: (item) => <Typography>{renderIcon(item)}</Typography>,
      disableSort: true,
      tableCellProps: {
        width: '30px',
        className: viewingMode === 'viewing' ? 'viewing-mode' : '',
      },
    },
    {
      key: 'id',
      label: viewingMode === 'viewing' ? '' : t('devicesTable.column.action'),
      renderCustomContentProvider: (item) => (
        <div>{viewingMode === 'viewing' ? <></> : <Typography>{renderLinkingAction(item)}</Typography>}</div>
      ),
      disableSort: true,
      tableCellProps: {
        className: viewingMode === 'viewing' ? 'viewing-mode' : '',
        align: 'right',
        width: '150px',
        style: { padding: 0 },
      },
    },
  ];

  const getTableData = (): RowItem<ResidentDeviceRowItem>[] => {
    const linkedDevicesRowItems =
      linkedDevices
        ?.map((linkedDevice) => ({
          ...linkedDevice,
          isLinked: true,
        }))
        .map<RowItem<ResidentDeviceRowItem>>((device) => ({
          data: device,
          isClickable: viewingMode === 'viewing',
          showColorOnHover: viewingMode === 'viewing',
        }))
        .sort((a, b) => a.data.name.localeCompare(b.data.name)) ?? [];

    const unlinkedDeviceRowItems =
      unlinkedDevices
        ?.map((unlinkedDevice) => ({
          ...unlinkedDevice,
          isLinked: false,
        }))
        .map<RowItem<ResidentDeviceRowItem>>((device) => ({
          data: device,
          isClickable: viewingMode === 'viewing',
          showColorOnHover: viewingMode === 'viewing',
        }))
        .sort((a, b) => a.data.name.localeCompare(b.data.name)) ?? [];

    return linkedDevicesRowItems.concat(unlinkedDeviceRowItems);
  };

  return (
    <div className={styles.residentDevicesContainer}>
      {getTableData().length > 0 ? (
        <Table className={styles.devicesTable} columns={columns} data={getTableData()} rowIdentifier={(item) => item.id} />
      ) : (
        <Typography variant="subtitle1">{t('noDevicesLinked')}</Typography>
      )}

      <Controller
        name="devices"
        control={residentSettingEditForm.control}
        render={({ field }) => (
          <DeviceLinkProvider
            organisationUnitId={currentResidentSetting?.organisationUnitId ?? ''}
            devicesToHide={field.value as LinkableDevice[]}
            transmitterResidentSettings={currentResidentSetting?.transmitterResidentSettings}
          >
            <DeviceLinkModal isOpen={linkDevicesModalIsOpen} onClose={closeLinkDevicesModal} onLinkButtonClicked={onDevicesLinkClicked} />
          </DeviceLinkProvider>
        )}
      />

      {(viewingMode === 'creation' || viewingMode === 'editing') && hasPermission(Permission.LinkDevice) && (
        <Button
          className={styles.linkDevicesButton}
          startIcon={<LinkOutlined />}
          variant="outlined"
          onClick={openLinkDevicesModal}
          data-testid="linkDeviceButton"
        >
          {t('linkDeviceButtonText')}
        </Button>
      )}
    </div>
  );
};

export default DetailsDevices;
